import { type UserState } from '@carvana/core-auth';
import { LocationData } from '@carvana/merch-geolocation';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo
} from 'react';
import { FavoriteVehicle } from '~/contracts/common';

export type UserContextType = {
  favorites: LoadableState<FavoriteVehicle[]>;
  location: LoadableState<Partial<LocationData>>;
  userState: LoadableState<Partial<UserState>>;
};

export const defaultUserContext: UserContextType = {
  favorites: defaultLoadableState<FavoriteVehicle[]>([]),
  location: defaultLoadableState<Partial<LocationData>>({}),
  userState: defaultLoadableState<Partial<UserState>>({})
};

const UserContext = createContext<UserContextType>(defaultUserContext);

export type OwnProps = PropsWithChildren<{
  location: Partial<LocationData>;
  userState?: Partial<UserState>;
}>;

export function UserContextProvider(props: OwnProps) {
  const favorites = useLoadableState<FavoriteVehicle[]>([]);
  const location = useLoadableState<Partial<LocationData>>(props.location);
  const userState = useLoadableState<Partial<UserState>>(props.userState ?? {});

  const value = useMemo(
    () => ({
      favorites,
      location,
      userState
    }),
    [favorites, location, userState]
  );

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
}

export function useFavorites(): FavoriteVehicle[] {
  return useContext(UserContext).favorites.data;
}

export function useFavoriteIds(): number[] {
  const favorites = useFavorites();

  return useMemo(
    () => favorites.map(favorite => favorite.vehicleId),
    [favorites]
  );
}

export function useIsLoggedIn(): UserState['isLoggedIn'] {
  return Boolean(useContext(UserContext).userState.data?.isLoggedIn);
}

export function useGetUser(): Partial<UserState['user']> {
  return useContext(UserContext).userState.data.user ?? {};
}

export function useGetLocation(): Partial<LocationData> {
  return useContext(UserContext).location.data ?? {};
}

UserContextProvider.displayName = 'UserContextProvider';

export default UserContext;
